<script setup>
import { ref, computed, onMounted } from 'vue';

const props = defineProps({
	isLanguageModalOpen: {
		type: Boolean,
		required: true,
	},
	updateContentLanguage: {
		type: Function,
		required: true,
	}
});
const activeTab = ref('chrome');

function setActiveTab(browserId) { activeTab.value = browserId; }

function runBrowserDetection() {
  const ua = navigator.userAgent
  let detected = 'generic'

  if (ua.indexOf("Edg/") > -1) {
    detected = 'edge'
  } else if (ua.indexOf("Chrome") > -1 && ua.indexOf("Chromium") > -1) {
    detected = 'chrome'
  } else if (ua.indexOf("Safari") > -1 && ua.indexOf("Chrome") === -1) {
    detected = 'safari'
  } else if (ua.indexOf("Firefox") > -1) {
    detected = 'firefox'
  }

  setActiveTab(detected !== 'generic' ? detected : 'chrome')
}

onMounted(() => {
  runBrowserDetection()
})
</script>

<template>
	<dialog
		id="language-modal"
		:data-open="isLanguageModalOpen"
		:open="isLanguageModalOpen"
	>
		<main>
			<header>
				<h2 class="mt-0">Translation Options</h2>
			</header>

			<div class="nav-tabs">
				<button
					class="tab"
					:class="{active: activeTab === 'chrome'}"
					@click="setActiveTab('chrome')"
				>Google Chrome</button>
				<button
					class="tab"
					:class="{active: activeTab === 'safari'}"
					@click="setActiveTab('safari')"
				>Safari</button>
				<button
					class="tab"
					:class="{active: activeTab === 'edge'}"
					@click="setActiveTab('edge')"
				>Microsoft Edge</button>
				<button
					class="tab"
					:class="{active: activeTab === 'firefox'}"
					@click="setActiveTab('firefox')"
				>Firefox</button>
				<button
					class="tab"
					:class="{active: activeTab === 'generic'}"
					@click="setActiveTab('generic')"
				>Other Browsers</button>
			</div>

			<div class="panel-content">
				<!-- Google Chrome Content -->
				<div class="instruction-pane" v-show="activeTab === 'chrome'">
					<ol class="instruction-steps">
						<li>Look at the right side of your address bar (near the top right) for the <strong>Translate icon</strong> (looks like a small G or a document grid).</li>
						<li>Click the icon and select your preferred language.</li>
						<li>Alternatively, <strong>right-click</strong> anywhere on an empty part of the page and select <strong>"Translate to [Language]"</strong> from the context menu.</li>
					</ol>
					<div class="tip-box">
						<strong>Chrome Tip:</strong> Click the three vertical dots within the translation box to check "Always translate" if you read pages in this language frequently.
					</div>
				</div>

				<!-- Safari Content -->
				<div class="instruction-pane" v-show="activeTab === 'safari'">
					<h3>On a Mac</h3>
					<ol class="instruction-steps">
						<li>Look at the Address field at the top of the window. There should be a <strong>Translate icon</strong> (two speech bubble squares overlapping) on the right side.</li>
						<li>Click the icon and select <strong>"Translate to [Your Language]"</strong>.</li>
						<li>If the icon isn't visible, click the menu bar at the top of your screen, navigate to <strong>View</strong>, and select <strong>Translation</strong>.</li>
					</ol>
					<h3>On iPhone / iPad</h3>
					<ol class="instruction-steps">
						<li>Look at the Address field at the top or bottom of the screen. There will be a small icon on the left side (a rectangle with two lines under it).</li>
						<li>Tap the icon and select <strong>"Translate to [Your Language]"</strong>.</li>
						<li>If the translation option isn't available, go to the <strong>Settings</strong> app. The Language options are in the "<strong>General</strong>" menu. About half-way down the page there's an option for "<strong>Language & Region</strong>" where you can choose your preferred languages.</li>
					</ol>
					<div class="tip-box">
						<strong>Safari Tip:</strong> If translation isn't enabled, ensure your preferred system languages are added in your Mac/iOS system settings.
					</div>
				</div>

				<!-- Microsoft Edge Content -->
				<div class="instruction-pane" v-show="activeTab === 'edge'">
					<ol class="instruction-steps">
						<li>Look inside the address bar at the top of the browser window for the <strong>Translate icon</strong> (looks like a character turning into an 'A').</li>
						<li>Click the icon to open the translation menu, choose your destination language, and click <strong>Translate</strong>.</li>
						<li>You can also <strong>right-click</strong> any blank area of the page and select <strong>"Translate page"</strong>.</li>
					</ol>
					<div class="tip-box">
						<strong>Edge Tip:</strong> Check the box that says "Always let Edge translate pages from this language" to automate future visits.
					</div>
				</div>

				<!-- Firefox Content -->
				<div class="instruction-pane" v-show="activeTab === 'firefox'">
					<ol class="instruction-steps">
						<li>Look at the right side of your address bar for the <strong>Translate icon</strong> (looks like a stylized map/page fold overlay).</li>
						<li>Click the icon to open Firefox's native, local translation prompt.</li>
						<li>Select your target language from the dropdown menu and click <strong>Translate</strong>.</li>
					</ol>
					<div class="tip-box">
						<strong>Firefox Tip:</strong> Firefox performs translation locally on your device, meaning your data stays private and isn't sent to an external cloud server.
					</div>
				</div>

				<!-- Generic Content Fallback -->
				<div class="instruction-pane" v-show="activeTab === 'generic'">
					<ol class="instruction-steps">
						<li>Most modern web browsers have a translation function located near or inside the <strong>Main Address Bar</strong> at the top of your window.</li>
						<li>Look for an icon representing text, languages, or gears, or check your browser's main menu settings (usually a three-dot or triple-bar icon).</li>
						<li>Alternatively, try <strong>right-clicking</strong> on an empty area of this webpage to see if a native <strong>"Translate"</strong> option appears in your context menu.</li>
					</ol>
					<div class="tip-box">
						<strong>Still need help?</strong> If your current browser doesn't support translation, we recommend accessing this site through Google Chrome, Apple Safari, or Microsoft Edge.
					</div>
				</div>
			</div>
		</main>
		<button class="btn primary close xl" @click="updateContentLanguage()">Close</button>
	</dialog>
	<div id="backdrop" @click="updateContentLanguage()"></div>
</template>

<style lang="scss" scoped>
#language-modal {
	background-color: transparent;
	border: none;
	color: var(--text1);
	display: flex;
	flex-flow: column;
	align-items: stretch;
	justify-content: center;
	margin: 0 auto;
	padding: 0;
	position: fixed;
	width: auto;
	max-width: min(600px, 94vw);
	height: 100dvh;
	z-index: 12;

	main {
		background-color: var(--bg1);
		border: 1px solid var(--border);
		border-radius: 24px;
		box-shadow: 0 30px 200px 40px rgba(0, 0, 0, 0.15), 0 30px 50px -30px rgba(0, 0, 0, 0.4), 0 30px 80px -50px rgba(0, 0, 0, 0.5);
		display: flex;
		flex-flow: column;

		padding: 0;

		@media(prefers-color-scheme:dark) {
			background-color: var(--bg3);
		}

		> * {
			padding-left: 24px;
			padding-right: 24px;
		}
	}
}

#backdrop {
	display: none;
	position: fixed;
	inset: 0;
	z-index: 11;
	background-color: oklch(from var(--bg3) l c h / .2);
	backdrop-filter: blur(5px) brightness(.7);
}
#language-modal[open] + #backdrop {display: block;}

:root {
	--badge-bg: #dbeafe;
	--badge-text: #1e40af;
}

header h2 {
	padding-top: 24px;

}

.nav-tabs {
	display: flex;
	border-bottom: 2px solid var(--border);
	gap: 8px;
	overflow-x: auto;
	overflow-y: visible;

	.tab {
		background: none;
		border: none;
		padding: 10px 14px;
		font-size: 14px;
		font-weight: 600;
		color: var(--text1);
		cursor: pointer;
		border-bottom: 2px solid transparent;
		margin-bottom: -2px;
		white-space: nowrap;
		transition: all 0.2s ease;

		&.active {
			color: var(--link);
			border-bottom-color: var(--link);
		}
	}
}

.panel-content {
	overflow: auto;
	max-height: calc(90dvh - 240px);
	padding:24px;

	h3:first-child {margin-top: 0;}
	h3:not(:first-child) {
		margin-top: 1.5em;
    padding-top: 1.5em;
    border-top: 2px solid var(--border);
  }
}

/* Instruction Content Panes */
.instruction-steps {
	padding-left: 20px;
	margin: 0 0 20px 0;

	li {
		margin-bottom: 12px;
		line-height: 1.5;
	}
	strong { color: var(--text1); }
}

.tip-box {
	background-color: var(--bg2);
	border-left: 4px solid var(--bg-button-primary);
	padding: 12px 16px;
	border-radius: 0 8px 8px 0;
	font-size: 14px;
	color: var(--text1);
	line-height: 1.4;
}

.close { margin-top: 1rem; }
</style>
